// Column → app-enum binding.
//
// Most of our "enums" are NOT Postgres enum types — they're `text` columns whose
// allowed values live in `@shared/types/enumValues` (with CHECK constraints
// mirroring them). Converting them to real DB enums is high-risk (reconcile/commission
// views compare them as text, generated columns + triggers reference them, and
// external feeds carry out-of-set values — see docs/plans/enum-audit.md). So
// instead the GENERATOR is taught which text columns are enums, here.
//
// Each binding carries the three things a generated enum field needs:
//   - `values` — allowed set, from `@shared/types/enumValues` (single source of truth)
//   - `tones`  — value → ToneName, from `@ds/tokens/domainTones` (single tone source)
//   - `labels` — value → Polish display label (single label source)
//
// `defaultField` (src/config/presentation.ts) consults this before its
// type-based ladder: an enum column yields a `select` edit (options labelled via
// `labels`) and a tone-coded `StatusChipShell` display — zero per-field authoring.

import type { ToneName } from "@ds/tokens/palette";
import {
  PRICE_TYPE_VALUES,
  GD_STATUS_VALUES,
  BORDER_TYPE_VALUES,
  TRANSPORT_TYPE_VALUES,
  DOCUMENT_STATUS_VALUES,
  REVIEW_STATUS_VALUES,
  INVOICE_REVIEW_STATUS_VALUES,
  CURRENCY_VALUES,
  INCOTERMS_VALUES,
  COMMISSION_TYPE_VALUES,
  EMPLOYMENT_TYPE_VALUES,
  EMPLOYEE_ROLE_VALUES,
  VAT_RATE_CODE_VALUES,
  CATEGORY_VALUES,
  PRICE_LIST_REQUEST_STATUS_VALUES,
} from "@shared/types/enumValues";
import { DELIVERY_TARGET_RECURRENCE_VALUES } from "@shared/types/delivery_target";
import { RECURRENCE_TONES } from "@ds/tokens/domainTones";
import {
  PRICE_TYPE_COLORS,
  GD_STATUS_COLORS,
  BORDER_TYPE_COLORS,
  TRANSPORT_TYPE_COLORS,
  DOCUMENT_STATUS_COLORS,
  REVIEW_STATUS_COLORS,
  INVOICE_REVIEW_STATUS_COLORS,
  CURRENCY_COLORS,
  INCOTERMS_COLORS,
  COMMISSION_TYPE_COLORS,
  EMPLOYMENT_TYPE_COLORS,
  ROLE_COLORS,
  VAT_RATE_COLORS,
  PRICE_LIST_SIDE_COLORS,
  CATEGORY_COLORS,
  PRICE_LIST_REQUEST_STATUS_COLORS,
} from "@ds/tokens/domainTones";

export interface ColumnEnum {
  /** Allowed values (authoritative — drives the `select` widget options). */
  values: readonly string[];
  /** value → ToneName, for consistent tone-coded display. */
  tones: Record<string, ToneName>;
  /** value → Polish display label (select option text + chip label). */
  labels: Record<string, string>;
}

/** Identity labels (codes display as-is, e.g. currency / incoterms). */
const idLabels = (vals: readonly string[]): Record<string, string> =>
  Object.fromEntries(vals.map((v) => [v, v]));

// ─── Reusable bindings (one per app enum) ──────────────────────────────────

const priceType: ColumnEnum = {
  values: PRICE_TYPE_VALUES, tones: PRICE_TYPE_COLORS,
  labels: { per_ton: "Za tonę", flat_rate: "Ryczałt" },
};
const gdStatus: ColumnEnum = {
  values: GD_STATUS_VALUES, tones: GD_STATUS_COLORS,
  labels: { yes: "GD", no: "IŹ", na: "NA" },
};
const borderType: ColumnEnum = {
  values: BORDER_TYPE_VALUES, tones: BORDER_TYPE_COLORS,
  labels: { domestic: "Krajowy", eu: "UE", non_eu: "Poza UE" },
};
const transportType: ColumnEnum = {
  values: TRANSPORT_TYPE_VALUES, tones: TRANSPORT_TYPE_COLORS,
  labels: { truck: "Samochód", train: "Pociąg", ship: "Statek", spedition: "Spedycja" },
};
const documentStatus: ColumnEnum = {
  values: DOCUMENT_STATUS_VALUES, tones: DOCUMENT_STATUS_COLORS,
  labels: { original: "Oryginał", copy: "Kopia", generated: "Wygenerowany" },
};
const reviewStatus: ColumnEnum = {
  values: REVIEW_STATUS_VALUES, tones: REVIEW_STATUS_COLORS,
  labels: { pending: "Oczekuje", approved: "Zatwierdzony", not_approved: "Niezatwierdzony", not_applicable: "Nie dotyczy" },
};
const invReviewStatus: ColumnEnum = {
  values: INVOICE_REVIEW_STATUS_VALUES, tones: INVOICE_REVIEW_STATUS_COLORS,
  labels: { pending: "Oczekuje", approved: "Zatwierdzona", rejected: "Odrzucona", partial: "Częściowa", request_correction: "Oczekuje na korektę" },
};
const currency: ColumnEnum = {
  values: CURRENCY_VALUES, tones: CURRENCY_COLORS, labels: idLabels(CURRENCY_VALUES),
};
const incotermsEnum: ColumnEnum = {
  values: INCOTERMS_VALUES, tones: INCOTERMS_COLORS, labels: idLabels(INCOTERMS_VALUES),
};
const commissionType: ColumnEnum = {
  values: COMMISSION_TYPE_VALUES, tones: COMMISSION_TYPE_COLORS,
  labels: { percentage: "Procent", per_ton: "Za tonę", flat_rate: "Ryczałt" },
};
const employmentType: ColumnEnum = {
  values: EMPLOYMENT_TYPE_VALUES, tones: EMPLOYMENT_TYPE_COLORS,
  labels: { employment: "Umowa o pracę", b2b: "B2B" },
};
const employeeRole: ColumnEnum = {
  values: EMPLOYEE_ROLE_VALUES, tones: ROLE_COLORS,
  labels: {
    admin: "Administrator", management: "Zarządzanie", sales: "Sprzedaż",
    administration: "Administracja", logistics: "Logistyka", developer: "Developer",
  },
};
const vatRateCode: ColumnEnum = {
  values: VAT_RATE_CODE_VALUES, tones: VAT_RATE_COLORS,
  labels: { "23": "23%", "8": "8%", "5": "5%", "0": "0%", zw: "zw.", np: "np." },
};
const side: ColumnEnum = {
  values: ["load", "unload"], tones: PRICE_LIST_SIDE_COLORS,
  labels: { load: "Załadunek", unload: "Rozładunek" },
};
const recurrence: ColumnEnum = {
  values: DELIVERY_TARGET_RECURRENCE_VALUES, tones: RECURRENCE_TONES,
  labels: { none: "Brak", daily: "Codziennie", weekly: "Co tydzień", monthly: "Co miesiąc", quarterly: "Co kwartał", yearly: "Co rok" },
};
const priceListRequestStatus: ColumnEnum = {
  values: PRICE_LIST_REQUEST_STATUS_VALUES, tones: PRICE_LIST_REQUEST_STATUS_COLORS,
  labels: { new: "Nowe", done: "Zrobione" },
};
const category: ColumnEnum = {
  values: CATEGORY_VALUES, tones: CATEGORY_COLORS,
  labels: {
    recycler: "Recykler", transporter: "Transporter", waste_generator: "Wytwórca",
    exporter: "Eksporter", importer: "Importer", organizacja_odzysku: "Org. odzysku",
    commercial: "Handlowy", other: "Inny",
  },
};

// ─── The map: table → column → binding ─────────────────────────────────────
//
// Deliberately excluded (NOT clean enums — see docs/plans/enum-audit.md):
//   invoices.payment_method        — free-text scraped from invoices
//   invoice_bank_accounts.purpose  — domain is full/net/vat_pln, not ContactPurpose
//   *.status                       — overloaded per-table value sets
//                                    (exception: price_list_requests.status is a
//                                    clean CHECK-constrained new/done pair)

export const COLUMN_ENUMS: Record<string, Record<string, ColumnEnum>> = {
  companies: { categories: category },
  price_lists: {
    price_type: priceType,
    gd_status: gdStatus,
    side,
    vat_rate_code: vatRateCode,
    currency,
    incoterms: incotermsEnum,
  },
  transactions: {
    border_type: borderType,
    gd_status: gdStatus,
    load_price_type: priceType,
    unload_price_type: priceType,
    load_price_vat_rate_code: vatRateCode,
    unload_price_vat_rate_code: vatRateCode,
  },
  transaction_transports: {
    transport_type: transportType,
    price_type: priceType,
    price_vat_rate_code: vatRateCode,
  },
  transaction_files: {
    document_status: documentStatus,
    review_status: reviewStatus,
  },
  invoices: {
    review_status: invReviewStatus,
    currency,
  },
  invoice_line_items: {
    vat_rate_code: vatRateCode,
    currency,
  },
  invoice_bank_accounts: { currency },
  company_bank_accounts: { currency },
  employees: { employment_type: employmentType, roles: employeeRole },
  commission_rules: {
    commission_type: commissionType,
    side,
  },
  commission_report_items: {
    commission_type: commissionType,
    side,
  },
  invoice_links: { side },
  company_sales_assignments: { side },
  edpr_rates: { gd_status: gdStatus },
  price_list_requests: { status: priceListRequestStatus, currency },
  delivery_targets: { recurrence },
};

/** The enum binding for `<table>.<column>`, or `undefined` if not an enum. */
export const columnEnum = (table: string, column: string): ColumnEnum | undefined =>
  COLUMN_ENUMS[table]?.[column];
